import { ComponentPropsWithoutRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import {
    faDiagramSubtask,
    faFileChartColumn,
    faListCheck,
    faMagicWandSparkles,
} from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from 'components/dialog';
import * as GivebutterPlusSplash from 'components/givebutter-plus-splash/GivebutterPlusSplash';
import { history as externalHistory } from 'components/router/history';
import { PLUS_PRICING_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import MrButterJumping from 'images/mr-butter-jumping.png';
import { twMerge } from 'tailwind-merge';

interface PromptPageProps extends ComponentPropsWithoutRef<'div'> {}
const PromptPage = ({ className, children, ...rest }: PromptPageProps) => {
    const { accountId, isFreeTrialExpired, validGivebutterPlusPlan } = useCurrentAccount();
    const history = useHistory();
    const promptToUpgrade = isFreeTrialExpired && !validGivebutterPlusPlan;

    return (
        <div className={twMerge('flex flex-wrap sm:flex-nowrap', className)} {...rest}>
            <div className="flex min-h-96 w-full flex-col justify-between p-4 sm:max-w-[23rem] sm:p-6">
                <img
                    src={MrButterJumping}
                    className="max-w-[4.5rem]"
                    alt="Mr. Butter jumping for joy"
                />
                <div className="flex flex-col gap-4">
                    <img
                        className={'w-full max-w-44'}
                        src={GivebutterPlusLogo}
                        alt="Givebutter Plus logo"
                    />
                    <div className="flex flex-col gap-2">
                        {promptToUpgrade ? (
                            <p className="text-xl">
                                <span className="font-medium">Your trial has ended.</span> Upgrade
                                now to unlock the full potential of Givebutter.
                            </p>
                        ) : (
                            <>
                                <p className="text-xl font-medium">Start your free Plus trial</p>
                                <p className="text-sm text-gray-600">
                                    Start your Givebutter Plus trial to send emails & texts to your
                                    supporters and unlock more benefits!
                                </p>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-3">
                    <Button
                        variant="solid"
                        size={'lg'}
                        className="w-full"
                        onClick={() => {
                            if (promptToUpgrade) {
                                externalHistory.push(
                                    `/accounts/${accountId}/settings/about/subscription`,
                                );
                            } else {
                                history.push('/form');
                            }
                        }}
                    >
                        {promptToUpgrade ? 'Upgrade' : 'Start my free trial'}
                    </Button>
                    <Dialog.Cancel asChild>
                        <Button size={'lg'} variant="outline" color="neutral">
                            {promptToUpgrade ? 'Not right now' : 'Maybe later'}
                        </Button>
                    </Dialog.Cancel>
                </div>
            </div>
            <FeatureDisplay />
        </div>
    );
};

const FeatureDisplay = () => {
    return (
        <div className="flex flex-col gap-4 bg-gray-50 p-4 sm:w-[480px] sm:gap-6 sm:p-8">
            <div className="flex gap-4 sm:gap-6">
                <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-1 size-8 shrink-0 sm:size-10">
                    <FontAwesomeIcon icon={faDiagramSubtask} />
                </GivebutterPlusSplash.FeatureCardIcon>
                <div className="flex flex-col sm:gap-2">
                    <div className="font-medium">Workflows</div>
                    <div className="text-xs text-gray-600 sm:text-base">
                        Automate reaching out to donors, tagging event attendees, sending cheerful
                        birthday messages, and so much more!
                    </div>
                </div>
            </div>

            <div className="flex gap-4 sm:gap-6">
                <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-3 size-8 shrink-0 sm:size-10">
                    <FontAwesomeIcon icon={faListCheck} />
                </GivebutterPlusSplash.FeatureCardIcon>
                <div className="flex flex-col sm:gap-2">
                    <div className="font-medium">Task management</div>
                    <div className="text-xs text-gray-600 sm:text-base">
                        Stay productive and organized effortlessly, ensuring you and your team stays
                        on top of responsibilities and priorities.
                    </div>
                </div>
            </div>

            <div className="flex gap-4 sm:gap-6">
                <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-1 size-8 shrink-0 sm:size-10">
                    <FontAwesomeIcon icon={faFileChartColumn} />
                </GivebutterPlusSplash.FeatureCardIcon>
                <div className="flex flex-col sm:gap-2">
                    <div className="font-medium">Custom reports</div>
                    <div className="text-xs text-gray-600 sm:text-base">
                        Create and automate customized reports to keep your team and stakeholders
                        informed.
                    </div>
                </div>
            </div>

            <div className="flex gap-4 sm:gap-6">
                <GivebutterPlusSplash.FeatureCardIcon className="bg-gbp-gradient-2 size-8 shrink-0 sm:size-10">
                    <FontAwesomeIcon icon={faMagicWandSparkles} />
                </GivebutterPlusSplash.FeatureCardIcon>
                <div className="flex flex-col sm:gap-2">
                    <div className="font-medium">Data Hygiene</div>
                    <div className="text-xs text-gray-600 sm:text-base">
                        Verify addresses, recognize data entry errors, and maintain spotless donor
                        information.
                    </div>
                </div>
            </div>

            <div className="text-sm text-gray-500 sm:text-base">
                View all features and prices on{' '}
                <a
                    className="font-normal text-gray-500 underline"
                    href={PLUS_PRICING_LINK}
                    target="_blank"
                    rel="noreferrer"
                >
                    Pricing page
                </a>
                .
            </div>
        </div>
    );
};

export { PromptPage };
